import React, { useState } from "react";
import { CheckCircle, Error } from "@mui/icons-material";
import { Paper, Tooltip } from "@mui/material";

import { NavigationButton } from "ui";

import { LayerStatus } from "fond/hooks/useGetImportLayers";
import { addImport } from "fond/redux/imports";
import { ImportStatus } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { LinearProgressWithLabel, SvgIcon } from "fond/widgets";

import { getBaseName, isgInputLayers } from "./files";
import UploadModal from "./UploadModal";

interface InputPanelProps {
  isReadOnly?: boolean;
  inputLayers: Map<string, LayerStatus>;
}

const InputPanel: React.FC<InputPanelProps> = ({ isReadOnly, inputLayers }) => {
  const dispatch = useAppDispatch();
  const versionId = useAppSelector((state) => state.project.versionId);
  const [uploadingLayer, setUploadingLayer] = useState<string | null>(null);

  const isAnyUploading = Array.from(inputLayers.values()).some(
    (layer) => layer.status && ["CONVERTING", "IMPORTING", "BUILDING_TILES"].includes(layer.status)
  );

  const handleUpload = (layerKey: string, files: File[]) => {
    const baseName = getBaseName(files[0]?.name || "");
    dispatch(
      addImport({
        versionId,
        layerKey,
        displayName: baseName || layerKey.split("/").pop() || "Layer",
        files,
      })
    );
    setUploadingLayer(null);
  };

  const getLayerConfig = (layerKey: string) => isgInputLayers.find((l) => l.key === layerKey);

  return (
    <>
      {isgInputLayers.map((layer) => {
        const layerStatus = inputLayers.get(layer.key);
        const hasLayer = !!layerStatus?.layer;
        const isError = layerStatus?.status === ImportStatus.ERROR;
        const isComplete = hasLayer && layerStatus?.status === ImportStatus.COMPLETE;

        return (
          <NavigationButton
            key={layer.key}
            data-testid={`isg-${layer.key.split("/").pop()}-input`}
            icon={<SvgIcon icon={layer.icon} />}
            title={layer.title}
            onClick={() => setUploadingLayer(layer.key)}
            sx={{ m: 0, mt: 1 }}
            disabled={isReadOnly || isAnyUploading}
            checked={isComplete}
            rightElement={
              isError ? (
                <Tooltip title="Import failed">
                  <Error color="error" sx={{ fontSize: 18 }} />
                </Tooltip>
              ) : isComplete ? (
                <CheckCircle color="success" sx={{ fontSize: 18 }} />
              ) : (
                <Tooltip title="Required">
                  <Error sx={{ color: "warning.main", fontSize: 18 }} />
                </Tooltip>
              )
            }
          />
        );
      })}

      {isAnyUploading && (
        <Paper variant="outlined" sx={{ py: 2, px: 2.75, mt: 2 }}>
          <LinearProgressWithLabel color="primary" variant="indeterminate" title="Importing layer" subtext="Processing..." />
        </Paper>
      )}

      {uploadingLayer && (
        <UploadModal
          layerTitle={getLayerConfig(uploadingLayer)?.title || "Layer"}
          onClose={() => setUploadingLayer(null)}
          onSubmit={(files) => handleUpload(uploadingLayer, files)}
        />
      )}
    </>
  );
};

export default InputPanel;
